import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
    GrdfAccessRightModel,
    GrdfContractualDataModel,
    GrdfRemoteAccessRightModel,
    GrdfTechnicalDataModel,
} from './grdf.models';
import { GrdfService } from './grdf.service';
import { AuthState } from 'src/app/core/states/auth.state';

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

// Store definition
export interface GrdfStateModel {
    grdfAccessRights: GrdfAccessRightModel[];
    grdfRemoteAccessRights: GrdfRemoteAccessRightModel[];
    currentAccessRight: GrdfAccessRightModel | undefined;
    currentTechnicalData: GrdfTechnicalDataModel | undefined;
    currentContractualData: GrdfContractualDataModel | undefined;
    isLoading: boolean;
    forceReload: boolean;
}

//Constants

export class GetAllGrdfAccessRights {
    static readonly type = 'GRDF/ACCESSRIGHT/ALL';
}

export class DeleteAccessRight {
    static readonly type = 'GRDF/ACCESSRIGHT/DELETE';
    constructor(public payload: { accessRightId: string }) {}
}

export class DeleteAccessRemoteRight {
    static readonly type = 'GRDF/ACCESSREMOTERIGHT/DELETE';
    constructor(public payload: { accessRightId: string }) {}
}

export class CreateAccessRight {
    static readonly type = 'GRDF/ACCESSRIGHT/CREATE';
    constructor(
        public payload: {
            accessRight: Partial<GrdfAccessRightModel>;
            fileProof?: File | null | undefined;
        },
    ) {}
}

export class UpdateFileProof {
    static readonly type = 'GRDF/FILEPROOF/UPDATE';
    constructor(public payload: { accessRightId: string; fileProof: File }) {}
}

export class UnsetCurrentAccessRightId {
    static readonly type = 'GRDF/ACCESSRIGHT/UNSET';
    constructor() {}
}

export class SetCurrentAccessRightId {
    static readonly type = 'GRDF/ACCESSRIGHT/SET';
    constructor(public payload: { id: string }) {}
}

export class GetTechnicalData {
    static readonly type = 'GRDF/TECHNICAL/GET';
    constructor(public payload: { accessRightId: string }) {}
}

export class UpdateGrdfForceReload {
    static readonly type = 'GRDF/FORCERELOAD';
    constructor(public payload: { forceReload: boolean }) {}
}

//Actions

@State<GrdfStateModel>({
    name: 'GrdfState',
    defaults: {
        grdfAccessRights: [],
        grdfRemoteAccessRights: [],
        currentAccessRight: undefined,
        currentTechnicalData: undefined,
        currentContractualData: undefined,
        isLoading: false,
        forceReload: false,
    },
})
@Injectable()
export class GrdfState {
    @Selector()
    public static grdfAccessRights(state: GrdfStateModel): GrdfAccessRightModel[] | null {
        return state.grdfAccessRights;
    }

    @Selector()
    public static forceReload(state: GrdfStateModel): boolean {
        return state.forceReload;
    }

    @Selector()
    public static grdfRemoteAccessRights(
        state: GrdfStateModel,
    ): GrdfRemoteAccessRightModel[] | null {
        return state.grdfRemoteAccessRights;
    }
    @Selector()
    public static currentAccessRight(state: GrdfStateModel): GrdfAccessRightModel | undefined {
        return state.currentAccessRight;
    }
    @Selector()
    public static currentAccessRightTechnicalData(
        state: GrdfStateModel,
    ): GrdfTechnicalDataModel | undefined {
        return state.currentTechnicalData;
    }
    @Selector()
    public static currentAccessRightContractualData(
        state: GrdfStateModel,
    ): GrdfContractualDataModel | undefined {
        return state.currentContractualData;
    }

    @Selector()
    static isLoading(state: GrdfStateModel): boolean {
        return state.isLoading;
    }

    constructor(
        private readonly grdfService: GrdfService,
        private readonly store: Store,
    ) {}

    @Action(UpdateGrdfForceReload)
    public async updateForceReload(
        ctx: StateContext<GrdfStateModel>,
        action: UpdateGrdfForceReload,
    ): Promise<void> {
        const forceReload = action.payload.forceReload;
        const currentState = ctx.getState();
        ctx.patchState({
            ...currentState,
            forceReload: forceReload,
        });
        //Force reload all data
        if (forceReload && currentState.currentAccessRight?.id) {
            ctx.dispatch(new SetCurrentAccessRightId({ id: currentState.currentAccessRight.id }));
        }
    }
    @Action(SetCurrentAccessRightId)
    public async setCurrentAccessRightId(
        ctx: StateContext<GrdfStateModel>,
        action: SetCurrentAccessRightId,
    ): Promise<void> {
        const currentState = ctx.getState();
        //Reload just in case
        if (currentState.grdfAccessRights.length == 0) {
            ctx.dispatch(GetAllGrdfAccessRights);
        }
        if (action.payload.id) {
            const currentAccessRight = await this.grdfService.getGrdfAccessRight(action.payload.id);
            const currentTechnicalData = await this.grdfService.getTechnicalData(
                action.payload.id,
                currentState.forceReload,
            );
            const currentContractualData = await this.grdfService.getContractualData(
                action.payload.id,
                currentState.forceReload,
            );
            ctx.patchState({
                ...currentState,
                currentAccessRight,
                currentTechnicalData,
                currentContractualData,
            });
        }
    }

    @Action(UnsetCurrentAccessRightId)
    public async unsetCurrentAccessRightId(
        ctx: StateContext<GrdfStateModel>,
        action: UnsetCurrentAccessRightId,
    ): Promise<void> {
        const currentState = ctx.getState();
        ctx.patchState({
            ...currentState,
            currentAccessRight: undefined,
            currentTechnicalData: undefined,
            currentContractualData: undefined,
        });
    }

    @Action(GetAllGrdfAccessRights)
    public async getAllAccessRights(
        ctx: StateContext<GrdfStateModel>,
        action: GetAllGrdfAccessRights,
    ): Promise<void> {
        let currentState = ctx.getState();
        ctx.patchState({ ...currentState, isLoading: true });

        const grdfAccessRights = await this.grdfService.getGrdfAccessRights();
        const loggedUser = this.store.selectSnapshot(AuthState.loggedUser);
        let grdfRemoteAccessRights: GrdfRemoteAccessRightModel[] = [];
        if (loggedUser?.isAdmin) {
            grdfRemoteAccessRights = await this.grdfService.getGrdfRemoteAccessRights();
        }
        currentState = ctx.getState();
        ctx.patchState({ ...State, grdfAccessRights, grdfRemoteAccessRights, isLoading: false });
    }

    @Action(CreateAccessRight)
    public async createAccessRight(
        ctx: StateContext<GrdfStateModel>,
        action: CreateAccessRight,
    ): Promise<void> {
        try {
            const msg = await this.grdfService.createGrdfAccessRight(
                action.payload.accessRight,
                action.payload.fileProof,
            );
            ctx.dispatch(GetAllGrdfAccessRights);
        } catch (err: any) {
            console.log(err?.error);
            throw err?.error;
        }
    }

    @Action(UpdateFileProof)
    public async updateFileProof(
        ctx: StateContext<GrdfStateModel>,
        action: UpdateFileProof,
    ): Promise<void> {
        try {
            const msg = await this.grdfService.postNewFileProof(
                action.payload.accessRightId,
                action.payload.fileProof,
            );
            ctx.dispatch(GetAllGrdfAccessRights);
        } catch (err: any) {
            console.log(err);
            throw new Error(err);
        }
    }

    @Action(DeleteAccessRight)
    public async deleteAccessRight(
        ctx: StateContext<GrdfStateModel>,
        action: DeleteAccessRight,
    ): Promise<void> {
        try {
            const msg = await this.grdfService.deleteGrdfAccessRight(action.payload.accessRightId);

            ctx.dispatch(GetAllGrdfAccessRights);
        } catch (err: any) {
            console.log(err);
        }
    }

    @Action(DeleteAccessRemoteRight)
    public async deleteAccessRemoteRight(
        ctx: StateContext<GrdfStateModel>,
        action: DeleteAccessRemoteRight,
    ): Promise<void> {
        try {
            const msg = await this.grdfService.deleteGrdfAccessRemoteRight(
                action.payload.accessRightId,
            );

            ctx.dispatch(GetAllGrdfAccessRights);
        } catch (err: any) {
            console.log(err);
        }
    }
}
